/**
 * Business Hours Component
 * Displays dynamic open/closed status based on business hours
 */

/**
 * Business Hours Alpine.js Component
 * Shows real-time open/closed status with hours until next opening
 */
export default function businessHours() {
  return {
    isOpen: false,
    closedMessage: 'Cerrado',
    updateInterval: null,

    // Business hours configuration
    config: {
      timezone: 'Europe/Madrid',
      openDays: [3, 4, 5, 6, 0], // Wed, Thu, Fri, Sat, Sun (0 = Sunday)
      openTime: { hour: 19, minute: 30 },
      closeTime: { hour: 23, minute: 0 },
    },

    init() {
      this.updateStatus();

      // Update every minute
      this.updateInterval = setInterval(() => {
        this.updateStatus();
      }, 60000);

      // Cleanup on component destroy
      this.$watch('updateInterval', (value) => {
        if (!value) {
          clearInterval(this.updateInterval);
        }
      });
    },

    /**
     * Get current date/time in Spain timezone
     */
    getCurrentTime() {
      return new Date(
        new Date().toLocaleString('en-US', {
          timeZone: this.config.timezone,
        })
      );
    },

    /**
     * Check if currently within business hours
     */
    checkIfOpen() {
      const now = this.getCurrentTime();
      const currentDay = now.getDay();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      // Check if today is an open day
      if (!this.config.openDays.includes(currentDay)) {
        return false;
      }

      // Convert current time and business hours to minutes for easier comparison
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      const openTimeInMinutes =
        this.config.openTime.hour * 60 + this.config.openTime.minute;
      const closeTimeInMinutes =
        this.config.closeTime.hour * 60 + this.config.closeTime.minute;

      return (
        currentTimeInMinutes >= openTimeInMinutes &&
        currentTimeInMinutes < closeTimeInMinutes
      );
    },

    /**
     * Calculate hours until next opening
     */
    calculateHoursUntilOpening() {
      const now = this.getCurrentTime();
      const currentDay = now.getDay();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      // Create opening time for today
      const todayOpening = new Date(now);
      todayOpening.setHours(
        this.config.openTime.hour,
        this.config.openTime.minute,
        0,
        0
      );

      // If today is an open day and we haven't reached opening time yet
      if (
        this.config.openDays.includes(currentDay) &&
        now < todayOpening
      ) {
        const diffMs = todayOpening - now;
        const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
        return diffHours;
      }

      // Find next opening day
      let daysUntilOpening = 0;
      let nextOpenDay = currentDay;

      do {
        daysUntilOpening++;
        nextOpenDay = (nextOpenDay + 1) % 7;

        // Safety check to prevent infinite loop
        if (daysUntilOpening > 7) {
          return null;
        }
      } while (!this.config.openDays.includes(nextOpenDay));

      // Calculate total hours until next opening
      const nextOpening = new Date(now);
      nextOpening.setDate(now.getDate() + daysUntilOpening);
      nextOpening.setHours(
        this.config.openTime.hour,
        this.config.openTime.minute,
        0,
        0
      );

      const diffMs = nextOpening - now;
      const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

      return diffHours;
    },

    /**
     * Update open/closed status and message
     */
    updateStatus() {
      this.isOpen = this.checkIfOpen();

      if (!this.isOpen) {
        const hoursUntilOpening = this.calculateHoursUntilOpening();

        // If more than 6 hours away, just show "Cerrado"
        if (hoursUntilOpening !== null && hoursUntilOpening <= 6) {
          if (hoursUntilOpening === 1) {
            this.closedMessage = 'Cerrado - Abrimos en 1 hora';
          } else {
            this.closedMessage = `Cerrado - Abrimos en ${hoursUntilOpening} horas`;
          }
        } else {
          this.closedMessage = 'Cerrado';
        }
      }
    },

    /**
     * Cleanup when component is destroyed
     */
    destroy() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    },
  };
}

// Register globally for Alpine.js
if (typeof window !== 'undefined') {
  window.businessHours = businessHours;
}
